import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'

function commandBinding(command) {
  return command?.effectiveBinding ?? command?.binding ?? null
}

function bindingParts(binding) {
  if (Array.isArray(binding)) return binding.filter(Boolean).map(String)
  if (binding === null || binding === undefined || binding === '') return []
  return [String(binding)]
}

function commandMatches(command, query) {
  const binding = bindingParts(commandBinding(command)).join(' ')
  return [command?.label, command?.description, command?.scope, binding]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(query)
}

function trapDialogFocus(event, container, first, last) {
  if (event.key !== 'Tab' || !container || !first || !last) return
  if (event.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function ShortcutKeys({ binding }) {
  return (
    <span className="flex shrink-0 items-center gap-1" aria-label={`Shortcut ${bindingParts(binding).join(' then ')}`}>
      {bindingParts(binding).map((part, index) => (
        <kbd
          key={`${part}-${index}`}
          data-testid="shortcut-key"
          className="min-w-7 rounded border border-border bg-canvas2 px-1.5 py-1 text-center font-mono text-[10px] font-semibold text-inkMuted shadow-sm"
        >
          {part}
        </kbd>
      ))}
    </span>
  )
}

export default function ShortcutHelp({
  open = true,
  commands = [],
  onClose,
  title = 'Keyboard shortcuts',
  initialQuery = '',
}) {
  const reactId = useId()
  const titleId = `shortcut-help-${reactId.replace(/:/g, '')}`
  const dialogRef = useRef(null)
  const searchRef = useRef(null)
  const closeRef = useRef(null)
  const [query, setQuery] = useState(initialQuery)

  const groups = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    const grouped = new Map()

    commands.forEach((command) => {
      const binding = commandBinding(command)
      if (bindingParts(binding).length === 0) return
      if (normalizedQuery && !commandMatches(command, normalizedQuery)) return

      const scope = command.scope || 'Global'
      const scopeLabel = command.scopeLabel || scope
      if (!grouped.has(scope)) grouped.set(scope, { scope, scopeLabel, commands: [] })
      grouped.get(scope).commands.push(command)
    })

    return Array.from(grouped.values())
  }, [commands, query])

  useEffect(() => {
    if (!open) return
    setQuery(initialQuery)
  }, [initialQuery, open])

  useEffect(() => {
    if (!open) return undefined
    const priorFocus = document.activeElement
    searchRef.current?.focus()
    return () => {
      if (priorFocus instanceof HTMLElement && priorFocus.isConnected) priorFocus.focus()
    }
  }, [open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-ink/20 px-4 pt-[8vh] backdrop-blur-[2px]"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.()
      }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[84vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-canvas shadow-2xl shadow-ink/20"
        onKeyDown={(event) => {
          trapDialogFocus(event, dialogRef.current, closeRef.current, searchRef.current)
          if (event.key !== 'Escape') return
          event.preventDefault()
          onClose?.()
        }}
      >
        <header className="border-b border-border px-5 pb-4 pt-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-inkMuted">Commands &amp; Navigation</div>
              <h2 id={titleId} className="mt-1 text-lg font-semibold text-ink">{title}</h2>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={() => onClose?.()}
              className="rounded-md p-2 text-inkMuted transition-colors hover:bg-canvas2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30"
              aria-label="Close keyboard shortcuts"
            >
              <X size={17} aria-hidden="true" />
            </button>
          </div>

          <div className="mt-4 flex items-center gap-2 rounded-lg border border-border bg-canvas2 px-3 focus-within:ring-2 focus-within:ring-ink/20">
            <Search size={15} className="shrink-0 text-inkMuted" aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              aria-label="Search shortcuts"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search shortcuts…"
              className="h-10 min-w-0 flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-inkDim"
            />
          </div>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {groups.length === 0 ? (
            <div role="status" className="py-12 text-center text-sm text-inkMuted">No shortcuts found</div>
          ) : (
            <div className="space-y-6">
              {groups.map(group => (
                <section key={group.scope} role="region" aria-label={group.scopeLabel}>
                  <h3 className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-inkMuted">
                    {group.scopeLabel}
                  </h3>
                  <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-canvas">
                    {group.commands.map(command => (
                      <div key={command.id || command.label} className="flex items-center gap-4 px-3 py-2.5">
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-ink">{command.label}</span>
                          {command.description && (
                            <span className="mt-0.5 block text-xs text-inkMuted">{command.description}</span>
                          )}
                          {command.unavailableReason && (
                            <span className="mt-0.5 block text-xs text-ember">{command.unavailableReason}</span>
                          )}
                        </span>
                        <ShortcutKeys binding={commandBinding(command)} />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>

        <footer className="border-t border-border bg-canvas2/60 px-5 py-2 text-right font-mono text-[10px] text-inkMuted">
          Esc Close
        </footer>
      </section>
    </div>
  )
}
